export var AttendanceStatus;
(function (AttendanceStatus) {
    /** There is no available attendance information. */
    AttendanceStatus["NOT_SET"] = "NOT_SET";
    /** At least a single participant attended the session. */
    AttendanceStatus["ATTENDED"] = "ATTENDED";
    /** No participants attended the session. */
    AttendanceStatus["NOT_ATTENDED"] = "NOT_ATTENDED";
})(AttendanceStatus || (AttendanceStatus = {}));
/**
 * A multi-service booking is considered available if all single-service bookings are available as returned from List Multi Service Availability Time Slots.
 * Currently, `SEPARATE_BOOKINGS` and `PARALLEL_BOOKINGS` are not supported.
 * Multi-service booking is available if each of its bookings is available separately.
 * For `SEQUENTIAL_BOOKINGS`, see `List Multi Service Availability Time Slots` documentation.
 */
export var MultiServiceBookingType;
(function (MultiServiceBookingType) {
    /** You must schedule single-service bookings back-to-back with each booking starting when the previous booking ends */
    MultiServiceBookingType["SEQUENTIAL_BOOKINGS"] = "SEQUENTIAL_BOOKINGS";
    /** Not currently supported. */
    MultiServiceBookingType["SEPARATE_BOOKINGS"] = "SEPARATE_BOOKINGS";
    /** Not currently supported. */
    MultiServiceBookingType["PARALLEL_BOOKINGS"] = "PARALLEL_BOOKINGS";
})(MultiServiceBookingType || (MultiServiceBookingType = {}));
export var LocationType;
(function (LocationType) {
    LocationType["UNDEFINED"] = "UNDEFINED";
    LocationType["OWNER_BUSINESS"] = "OWNER_BUSINESS";
    LocationType["OWNER_CUSTOM"] = "OWNER_CUSTOM";
    LocationType["CUSTOM"] = "CUSTOM";
})(LocationType || (LocationType = {}));
export var ValueType;
(function (ValueType) {
    /** Short text. This is the default value type. */
    ValueType["SHORT_TEXT"] = "SHORT_TEXT";
    /** Long text. */
    ValueType["LONG_TEXT"] = "LONG_TEXT";
    /** A text that represents the check box value. If selected the value is `true`, otherwise `false`. */
    ValueType["CHECK_BOX"] = "CHECK_BOX";
})(ValueType || (ValueType = {}));
/** Booking status. */
export var BookingStatus;
(function (BookingStatus) {
    /** The booking was created, but the customer hasn't completed the related eCommerce order yet. */
    BookingStatus["CREATED"] = "CREATED";
    /**
     * The merchant has confirmed the booking and it appears in the business calendar. Merchants can set up their services
     * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/setup) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction)) to automatically confirm all `PENDING` bookings.
     */
    BookingStatus["CONFIRMED"] = "CONFIRMED";
    /**
     * The customer has canceled the booking. Depending on the relevant service's policy snapshot
     * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/booking-policy-snapshots/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policy-snapshots/introduction))
     * they may have to pay a cancellation fee ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/booking-fees/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/booking-fees/introduction)).
     */
    BookingStatus["CANCELED"] = "CANCELED";
    /** The merchant must manually confirm the booking before it appears in the business calendar. */
    BookingStatus["PENDING"] = "PENDING";
    /** The merchant has declined the booking before the customer was charged. */
    BookingStatus["DECLINED"] = "DECLINED";
    /**
     * The booking is on a waitlist.
     * Currently, you can't call Register to Waitlist ([REST](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings/waitlist/register-to-waitlist)) for course or appointment bookings, even though this is supported in live sites.
     * You can call Register to Waitlist only for class session bookings.
     */
    BookingStatus["WAITING_LIST"] = "WAITING_LIST";
})(BookingStatus || (BookingStatus = {}));
/**
 * Payment status.
 * Automatically updated when using eCommerce checkout APIs.
 */
export var PaymentStatus;
(function (PaymentStatus) {
    /** Undefined payment status. */
    PaymentStatus["UNDEFINED"] = "UNDEFINED";
    /** The booking isn't paid. */
    PaymentStatus["NOT_PAID"] = "NOT_PAID";
    /** The booking is fully paid. */
    PaymentStatus["PAID"] = "PAID";
    /** The booking is partially paid. */
    PaymentStatus["PARTIALLY_PAID"] = "PARTIALLY_PAID";
    /** The booking is refunded. */
    PaymentStatus["REFUNDED"] = "REFUNDED";
    /** The booking is free of charge. */
    PaymentStatus["EXEMPT"] = "EXEMPT";
})(PaymentStatus || (PaymentStatus = {}));
/**
 * Selected payment option.
 *
 * One of the payment options offered by the service.
 * This field is set when the user selects an option during booking.
 * If left undefined, the payment option is resolved by the service configuration on checkout.
 */
export var SelectedPaymentOption;
(function (SelectedPaymentOption) {
    /** Undefined payment option. */
    SelectedPaymentOption["UNDEFINED"] = "UNDEFINED";
    /** Offline payment. */
    SelectedPaymentOption["OFFLINE"] = "OFFLINE";
    /** Online payment. */
    SelectedPaymentOption["ONLINE"] = "ONLINE";
    /** Payment using a Wix Pricing Plan. */
    SelectedPaymentOption["MEMBERSHIP"] = "MEMBERSHIP";
    /**
     * Customers can pay only in person with a Wix Pricing Plan, while the Wix user
     * must manually redeem the pricing plan in the dashboard.
     */
    SelectedPaymentOption["MEMBERSHIP_OFFLINE"] = "MEMBERSHIP_OFFLINE";
})(SelectedPaymentOption || (SelectedPaymentOption = {}));
export var Platform;
(function (Platform) {
    Platform["UNDEFINED_PLATFORM"] = "UNDEFINED_PLATFORM";
    Platform["WEB"] = "WEB";
    Platform["MOBILE_APP"] = "MOBILE_APP";
})(Platform || (Platform = {}));
export var Actor;
(function (Actor) {
    Actor["UNDEFINED_ACTOR"] = "UNDEFINED_ACTOR";
    Actor["BUSINESS"] = "BUSINESS";
    Actor["CUSTOMER"] = "CUSTOMER";
})(Actor || (Actor = {}));
export var IdentityType;
(function (IdentityType) {
    IdentityType["UNKNOWN"] = "UNKNOWN";
    IdentityType["ANONYMOUS_VISITOR"] = "ANONYMOUS_VISITOR";
    IdentityType["MEMBER"] = "MEMBER";
    IdentityType["WIX_USER"] = "WIX_USER";
    IdentityType["APP"] = "APP";
})(IdentityType || (IdentityType = {}));
export var BookingFeeStatus;
(function (BookingFeeStatus) {
    /** There is no information about the booking fee status. */
    BookingFeeStatus["UNKNOWN_STATUS"] = "UNKNOWN_STATUS";
    /** The booking fee hasn't been applied to the eCommerce order yet. */
    BookingFeeStatus["NOT_YET_APPLIED_TO_ORDER"] = "NOT_YET_APPLIED_TO_ORDER";
    /** The booking fee has already been applied as an additional fee to the eCommerce order. */
    BookingFeeStatus["APPLIED_TO_ORDER"] = "APPLIED_TO_ORDER";
})(BookingFeeStatus || (BookingFeeStatus = {}));
/** Type of selected payment option for catalog item */
export var PaymentOptionType;
(function (PaymentOptionType) {
    /** The entire payment for this item happens as part of the checkout. */
    PaymentOptionType["FULL_PAYMENT_ONLINE"] = "FULL_PAYMENT_ONLINE";
    /** The entire payment for this item happens after checkout. For example, when using cash, check, or other offline payment methods. */
    PaymentOptionType["FULL_PAYMENT_OFFLINE"] = "FULL_PAYMENT_OFFLINE";
    /** Payment for this item is done by charging a membership. Any remaining amount not covered by the membership, such as item modifiers, is paid online. */
    PaymentOptionType["MEMBERSHIP"] = "MEMBERSHIP";
    /** Partial payment to be paid upfront during checkout. The initial amount to be paid for each line item is specified in `depositAmount`. */
    PaymentOptionType["DEPOSIT_ONLINE"] = "DEPOSIT_ONLINE";
    /** Payment for this item can only be done by charging a membership and must be manually redeemed in the dashboard by the site admin. When selected, `price` is `0`. */
    PaymentOptionType["MEMBERSHIP_OFFLINE"] = "MEMBERSHIP_OFFLINE";
    /**
     * Item price is charged to online membership. Any remaining amount not covered by the membership, such as item modifiers, is paid offline.
     * @documentationMaturity preview
     */
    PaymentOptionType["MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER"] = "MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER";
})(PaymentOptionType || (PaymentOptionType = {}));
export var OrderStatus;
(function (OrderStatus) {
    /** Order created, but not yet approved or canceled. */
    OrderStatus["INITIALIZED"] = "INITIALIZED";
    /**
     * Order approved.
     *
     * This happens when either an online payment is received **or** when `order.priceSummary.total = 0` (a zero-total order).
     * Offline orders (cash payment) are automatically approved.
     */
    OrderStatus["APPROVED"] = "APPROVED";
    /** Order canceled by the user. */
    OrderStatus["CANCELED"] = "CANCELED";
    /**
     * Order pending.
     * @documentationMaturity preview
     */
    OrderStatus["PENDING"] = "PENDING";
    /**
     * Order rejected.
     *
     * This happens when pending payments fail.
     * @documentationMaturity preview
     */
    OrderStatus["REJECTED"] = "REJECTED";
})(OrderStatus || (OrderStatus = {}));
export var PaymentStatusEnumPaymentStatus;
(function (PaymentStatusEnumPaymentStatus) {
    PaymentStatusEnumPaymentStatus["UNSPECIFIED"] = "UNSPECIFIED";
    /**
     * `NOT_PAID` can apply to an order made online, but not yet paid. In such cases `order.status` will be `INITIALIZED`.
     * This status also applies when an offline order needs to be manually marked as paid. In such cases `order.status` will be `APPROVED`.
     */
    PaymentStatusEnumPaymentStatus["NOT_PAID"] = "NOT_PAID";
    /** All required payments associated with this order are paid. */
    PaymentStatusEnumPaymentStatus["PAID"] = "PAID";
    /** Order partially refunded, but the refunded amount is less than the order's total price. See `order.balanceSummary` for more details. */
    PaymentStatusEnumPaymentStatus["PARTIALLY_REFUNDED"] = "PARTIALLY_REFUNDED";
    /** Order fully refunded. Refund amount equals total price. See `order.balanceSummary` for more details. */
    PaymentStatusEnumPaymentStatus["FULLY_REFUNDED"] = "FULLY_REFUNDED";
    /**
     * All payments pending.
     *
     * This can happen with two-step payments, when a payment requires manual review, or when a payment is in progress and will be concluded shortly.
     * Learn more about [pending orders](https://support.wix.com/en/article/pending-orders).
     */
    PaymentStatusEnumPaymentStatus["PENDING"] = "PENDING";
    /** At least one payment received and approved, but it covers less than the order's total price. See `order.balanceSummary` for more details. */
    PaymentStatusEnumPaymentStatus["PARTIALLY_PAID"] = "PARTIALLY_PAID";
    /**
     * Payment received, but not yet confirmed by the payment provider.
     *
     * In most cases, when a payment provider is holding payment it's because setup hasn't been successfully completed by the merchant/site owner.
     * To solve this, the merchant/site owner should log in to the payment provider's dashboard and make sure their account is set up correctly, or contact their support for further assistance.
     * @documentationMaturity preview
     */
    PaymentStatusEnumPaymentStatus["PENDING_MERCHANT"] = "PENDING_MERCHANT";
    /**
     * One or more payments canceled.
     * @documentationMaturity preview
     */
    PaymentStatusEnumPaymentStatus["CANCELED"] = "CANCELED";
    /**
     * One or more payments declined.
     * @documentationMaturity preview
     */
    PaymentStatusEnumPaymentStatus["DECLINED"] = "DECLINED";
})(PaymentStatusEnumPaymentStatus || (PaymentStatusEnumPaymentStatus = {}));
export var Status;
(function (Status) {
    Status["UNDEFINED"] = "UNDEFINED";
    Status["SIGNED"] = "SIGNED";
    Status["NOT_SIGNED"] = "NOT_SIGNED";
})(Status || (Status = {}));
export var TransactionStatus;
(function (TransactionStatus) {
    TransactionStatus["UNDEFINED"] = "UNDEFINED";
    TransactionStatus["APPROVED"] = "APPROVED";
    TransactionStatus["PENDING"] = "PENDING";
    TransactionStatus["PENDING_MERCHANT"] = "PENDING_MERCHANT";
    TransactionStatus["CANCELED"] = "CANCELED";
    TransactionStatus["DECLINED"] = "DECLINED";
    TransactionStatus["REFUNDED"] = "REFUNDED";
    TransactionStatus["PARTIALLY_REFUNDED"] = "PARTIALLY_REFUNDED";
    TransactionStatus["AUTHORIZED"] = "AUTHORIZED";
    TransactionStatus["VOIDED"] = "VOIDED";
})(TransactionStatus || (TransactionStatus = {}));
export var AuthorizationCaptureStatus;
(function (AuthorizationCaptureStatus) {
    AuthorizationCaptureStatus["UNKNOWN_STATUS"] = "UNKNOWN_STATUS";
    /** Capture operation still in progress. */
    AuthorizationCaptureStatus["PENDING"] = "PENDING";
    /** Capture operation succeeded. */
    AuthorizationCaptureStatus["SUCCEEDED"] = "SUCCEEDED";
    /** Capture operation failed. */
    AuthorizationCaptureStatus["FAILED"] = "FAILED";
})(AuthorizationCaptureStatus || (AuthorizationCaptureStatus = {}));
export var AuthorizationVoidStatus;
(function (AuthorizationVoidStatus) {
    AuthorizationVoidStatus["UNKNOWN_STATUS"] = "UNKNOWN_STATUS";
    /** Void operation still in progress. */
    AuthorizationVoidStatus["PENDING"] = "PENDING";
    /** Void operation succeeded. */
    AuthorizationVoidStatus["SUCCEEDED"] = "SUCCEEDED";
    /** Void operation failed. */
    AuthorizationVoidStatus["FAILED"] = "FAILED";
})(AuthorizationVoidStatus || (AuthorizationVoidStatus = {}));
/** Reason the authorization was voided. */
export var Reason;
(function (Reason) {
    Reason["UNKNOWN_REASON"] = "UNKNOWN_REASON";
    /** Authorization was voided by user. */
    Reason["MANUAL"] = "MANUAL";
    /** Authorization passed execution date. */
    Reason["SCHEDULED"] = "SCHEDULED";
})(Reason || (Reason = {}));
export var ActionType;
(function (ActionType) {
    ActionType["UNKNOWN_ACTION_TYPE"] = "UNKNOWN_ACTION_TYPE";
    ActionType["VOID"] = "VOID";
    ActionType["CAPTURE"] = "CAPTURE";
})(ActionType || (ActionType = {}));
export var ChargebackStatus;
(function (ChargebackStatus) {
    ChargebackStatus["UNSPECIFIED"] = "UNSPECIFIED";
    /** Chargeback was approved. */
    ChargebackStatus["APPROVED"] = "APPROVED";
    /** Chargeback was reversed. */
    ChargebackStatus["REVERSED"] = "REVERSED";
})(ChargebackStatus || (ChargebackStatus = {}));
export var MembershipPaymentStatus;
(function (MembershipPaymentStatus) {
    /** Payment was charged. */
    MembershipPaymentStatus["CHARGED"] = "CHARGED";
    /** The attempt to charge the payment failed, for example, due to lack of credits. */
    MembershipPaymentStatus["CHARGE_FAILED"] = "CHARGE_FAILED";
})(MembershipPaymentStatus || (MembershipPaymentStatus = {}));
/** Refund transaction status. */
export var RefundStatus;
(function (RefundStatus) {
    /** Refund was initiated on payment provider side. PENDING status was assigned by provider. */
    RefundStatus["PENDING"] = "PENDING";
    /** Refund transaction succeeded. */
    RefundStatus["SUCCEEDED"] = "SUCCEEDED";
    /** Refund transaction failed. */
    RefundStatus["FAILED"] = "FAILED";
    /** Refund request acknowledged, and will be executed soon. */
    RefundStatus["SCHEDULED"] = "SCHEDULED";
    /** Refund was initiated on payment provider side. */
    RefundStatus["STARTED"] = "STARTED";
})(RefundStatus || (RefundStatus = {}));
export var SortOrder;
(function (SortOrder) {
    SortOrder["ASC"] = "ASC";
    SortOrder["DESC"] = "DESC";
})(SortOrder || (SortOrder = {}));
//# sourceMappingURL=types.impl.js.map